import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNowStrict, parseISO } from "date-fns";
import { ChevronRight } from "lucide-react";
import { Notice } from "~/components/notice";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { api } from "~/lib/api";
import { parcelTitle } from "~/lib/parcel-status";
import type { IParcel, ITicket } from "~/lib/proto";

export const Route = createFileRoute("/_authed/_portal/messages")({
  component: Messages,
});

type Thread = { parcelId: string; title: string; lastActivity: string; unread: number };

function ago(iso: string): string {
  const date = parseISO(iso);
  return Number.isNaN(date.getTime()) ? "" : `${formatDistanceToNowStrict(date)} ago`;
}

function threadsFrom(tickets: ITicket[], parcels: IParcel[], unread: Record<string, number>): Thread[] {
  const titles = new Map(parcels.map((parcel) => [parcel.id ?? "", parcelTitle(parcel)]));
  const latest = new Map<string, string>();
  for (const ticket of tickets) {
    const parcelId = ticket.parcelId ?? "";
    const activity = ticket.updatedAt ?? ticket.createdAt ?? "";
    if (activity > (latest.get(parcelId) ?? "")) latest.set(parcelId, activity);
  }
  return [...latest.entries()]
    .map(([parcelId, lastActivity]) => ({
      parcelId,
      lastActivity,
      title: titles.get(parcelId) ?? "Parcel",
      unread: unread[parcelId] ?? 0,
    }))
    .sort((a, b) => b.unread - a.unread || b.lastActivity.localeCompare(a.lastActivity));
}

function Messages() {
  const tickets = useQuery({ queryKey: ["my-tickets"], queryFn: () => api.myTickets() });
  const active = useQuery({ queryKey: ["parcels"], queryFn: () => api.listParcels(false) });
  const delivered = useQuery({ queryKey: ["parcels", "archived"], queryFn: () => api.listParcels(true) });
  const unread = useQuery({ queryKey: ["my-unread"], queryFn: () => api.unread(), staleTime: 3000 });

  const loading = tickets.isLoading || active.isLoading || delivered.isLoading;
  const threads = threadsFrom(tickets.data ?? [], [...(active.data ?? []), ...(delivered.data ?? [])], unread.data?.parcel ?? {});

  return (
    <div className="space-y-5">
      <h1 className="type-title">Messages</h1>

      {loading ? (
        <div className="space-y-3" aria-busy>
          <Skeleton className="h-16 rounded-xl" />
          <Skeleton className="h-16 rounded-xl" />
        </div>
      ) : tickets.isError ? (
        <Notice title="We could not load your messages" body="Check your connection, then try again.">
          <Button variant="outline" className="h-11 px-5 text-[15px]" onClick={() => void tickets.refetch()}>
            Try again
          </Button>
        </Notice>
      ) : threads.length === 0 ? (
        <Notice title="No messages yet" body="To ask about a parcel, open it and write to our team from there." />
      ) : (
        <ul className="border-hairline bg-card divide-hairline divide-y overflow-hidden rounded-xl border">
          {threads.map((thread) => (
            <li key={thread.parcelId}>
              <Link
                to="/parcel/$id"
                params={{ id: thread.parcelId }}
                hash="messages"
                className="hover:bg-accent/30 flex min-h-16 items-center gap-3 px-4 py-3 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[16px] font-medium">{thread.title}</p>
                  <p className="text-muted-foreground text-[13px]">
                    {thread.unread > 0 ? "Our team replied" : "Last message"} {ago(thread.lastActivity)}
                  </p>
                </div>
                {thread.unread > 0 && (
                  <span className="bg-primary text-primary-foreground tabular grid h-6 min-w-6 place-items-center rounded-full px-1.5 text-[12px] font-medium">
                    {thread.unread}
                  </span>
                )}
                <ChevronRight className="text-muted-foreground size-5 shrink-0" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
